// Design tokens shared between iOS and Web.
// Colors, gradients, and constants are derived from the iOS app's
// Colors.swift and PatientHubView.swift to ensure visual consistency.

import {
  SUPPORTED_LANGUAGES,
  TELEPHONIC_SUPPORTED_LANGUAGES,
} from '../common/languages';

// ── Brand ────────────────────────────────────────────────────
export const OPALITE_BLUE = '#1E9FD4';

// ── Semantic accent colors (matches iOS PatientHubView badges) ─
// Scribe and live encounter accents stay visually distinct; the
// hues are chosen to be visually orthogonal so a provider scanning
// the session list can tell the two types apart at a glance.
export const SCRIBE_COLOR = '#6366F1';
export const ENCOUNTER_COLOR = '#5EAF96';
// Telephonic (phone encounter) accent. A distinct teal reserved for
// company-shared phone calls so they never read as a personal scribe or
// live encounter in the Encounters list.
export const TELEPHONIC_COLOR = '#0EA5B7';
// Video (meeting-bot encounter) accent — Recall.ai / RingCentral bots joining
// a Zoom/Meet/Teams call. A distinct violet so video sessions read apart from
// in-person encounters, scribe, and phone calls in the Encounters list.
export const VIDEO_COLOR = '#7C5CFF';
export const EHR_GREEN = '#22A06B';
export const REC_RED = '#E5484D';

// ── Avatar gradient pairs (matches iOS patientAvatarColors) ──
// Each pair is [topLeading, bottomTrailing] for a linear-gradient.
export const AVATAR_GRADIENT_PAIRS = [
  ['#1E9FD4', '#1786B4'], // Brand blue
  ['rgb(143, 169, 140)', 'rgb(112, 141, 110)'], // Muted sage green
  ['rgb(201, 139, 146)', 'rgb(175, 110, 118)'], // Dusty rose
  ['rgb(139, 143, 190)', 'rgb(110, 114, 166)'], // Soft slate purple
  ['rgb(187, 163, 138)', 'rgb(160, 134, 108)'], // Warm taupe
];

export function avatarFlatColor(index: number): string {
  return AVATAR_GRADIENT_PAIRS[index % AVATAR_GRADIENT_PAIRS.length][0];
}

// ── Language flag helper ─────────────────────────────────────
const FLAG_FALLBACK = '\u{1F310}';
const REGIONAL_INDICATOR_BASE = 0x1f1e6;
const LATIN_CAPITAL_A = 0x41;
const TAG_BASE = 0xe0000;
const TAG_CANCEL = '\u{E007F}';

export function unicodeRegionFlag(
  regionCode: string | null | undefined,
): string {
  const normalized = (regionCode || '').trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(normalized)) return FLAG_FALLBACK;
  return Array.from(normalized)
    .map(letter =>
      String.fromCodePoint(
        REGIONAL_INDICATOR_BASE + letter.charCodeAt(0) - LATIN_CAPITAL_A,
      ),
    )
    .join('');
}

export function unicodeSubdivisionFlag(
  subdivisionCode: string | null | undefined,
): string {
  const normalized = (subdivisionCode || '')
    .trim()
    .toLowerCase()
    .replace(/-/g, '');
  if (!/^[a-z0-9]{3,30}$/.test(normalized)) return FLAG_FALLBACK;
  return `\u{1F3F4}${Array.from(normalized)
    .map(char => String.fromCodePoint(TAG_BASE + char.charCodeAt(0)))
    .join('')}${TAG_CANCEL}`;
}

const LANGUAGE_FLAG_CODES: Record<string, string> = {
  'afghan persian': 'AF',
  'african french': 'SN',
  'african portuguese': 'AO',
  afrikaans: 'ZA',
  albanian: 'AL',
  amharic: 'ET',
  'american english': 'US',
  arabic: 'SA',
  'argentinian spanish': 'AR',
  armenian: 'AM',
  'austrian german': 'AT',
  awadhi: 'IN',
  azerbaijani: 'AZ',
  basque: 'ES',
  belarusian: 'BY',
  bengali: 'BD',
  bokmal: 'NO',
  bosnian: 'BA',
  'braj bhasha': 'IN',
  'brazilian portuguese': 'BR',
  'british english': 'GB',
  bulgarian: 'BG',
  burmese: 'MM',
  'canadian french quebec': 'CA',
  'cape verdean creole': 'CV',
  'caribbean spanish': 'CU',
  castilian: 'ES',
  catalan: 'AD',
  cantonese: 'HK',
  'cebuano tagalog': 'PH',
  'central thai': 'TH',
  'central yiddish': 'IL',
  chittagonian: 'BD',
  chinese: 'CN',
  colombian: 'CO',
  'colombian spanish': 'CO',
  'congo swahili': 'CD',
  croatian: 'HR',
  czech: 'CZ',
  danish: 'DK',
  dari: 'AF',
  'dominican spanish': 'DO',
  dutch: 'NL',
  'eastern armenian': 'AM',
  'eastern yiddish': 'IL',
  egyptian: 'EG',
  'egyptian arabic': 'EG',
  english: 'US',
  estonian: 'EE',
  'european portuguese': 'PT',
  'european spanish castilian': 'ES',
  farsi: 'IR',
  finnish: 'FI',
  french: 'FR',
  galician: 'ES',
  georgian: 'GE',
  german: 'DE',
  greek: 'GR',
  guatemalan: 'GT',
  'guatemalan spanish': 'GT',
  gujarati: 'IN',
  'gulf arabic': 'AE',
  haitian: 'HT',
  'haitian creole': 'HT',
  hebrew: 'IL',
  hindi: 'IN',
  hokkien: 'TW',
  hungarian: 'HU',
  indonesian: 'ID',
  'iranian persian farsi': 'IR',
  italian: 'IT',
  japanese: 'JP',
  'jakarta indonesian': 'ID',
  jeju: 'KR',
  'jeju dialect': 'KR',
  kabyle: 'DZ',
  kannada: 'IN',
  kansai: 'JP',
  kazakh: 'KZ',
  'kenyan swahili': 'KE',
  korean: 'KR',
  kurdish: 'IQ',
  'kurdish sorani': 'IQ',
  kyrgyz: 'KG',
  latvian: 'LV',
  levantine: 'LB',
  'levantine arabic': 'LB',
  lithuanian: 'LT',
  luganda: 'UG',
  maay: 'SO',
  'maay somali': 'SO',
  macedonian: 'MK',
  malay: 'MY',
  malayalam: 'IN',
  mandarin: 'CN',
  'mandarin simplified': 'CN',
  'mandarin traditional': 'TW',
  'manila tagalog': 'PH',
  marathi: 'IN',
  'metropolitan french': 'FR',
  'mexican spanish': 'MX',
  'modern hebrew': 'IL',
  'modern standard arabic': 'SA',
  myanmar: 'MM',
  nepali: 'NP',
  'northern italian': 'IT',
  'northern thai': 'TH',
  'northern vietnamese': 'VN',
  norwegian: 'NO',
  nynorsk: 'NO',
  odia: 'IN',
  okinawan: 'JP',
  pashto: 'AF',
  persian: 'IR',
  polish: 'PL',
  portuguese: 'BR',
  'puerto rican': 'PR',
  'puerto rican spanish': 'PR',
  quebec: 'CA',
  romanian: 'RO',
  russian: 'RU',
  seoul: 'KR',
  'seoul dialect': 'KR',
  sephardic: 'IL',
  'sephardic hebrew': 'IL',
  serbian: 'RS',
  sicilian: 'IT',
  slovak: 'SK',
  slovenian: 'SI',
  somali: 'SO',
  'southern russian': 'RU',
  'southern thai': 'TH',
  'southern vietnamese': 'VN',
  spanish: 'ES',
  'standard bengali': 'BD',
  'standard german': 'DE',
  'standard hindi': 'IN',
  'standard indonesian': 'ID',
  'standard italian': 'IT',
  'standard japanese': 'JP',
  'standard korean': 'KR',
  'standard russian': 'RU',
  'standard somali': 'SO',
  'standard turkish': 'TR',
  swahili: 'KE',
  swedish: 'SE',
  'swiss german': 'CH',
  sylheti: 'BD',
  tagalog: 'PH',
  tajik: 'TJ',
  'tajik persian': 'TJ',
  tamil: 'IN',
  tanzanian: 'TZ',
  'tanzanian swahili': 'TZ',
  tatar: 'RU',
  telugu: 'IN',
  thai: 'TH',
  turkish: 'TR',
  'uk english': 'GB',
  ukrainian: 'UA',
  urdu: 'PK',
  'us english': 'US',
  uzbek: 'UZ',
  vietnamese: 'VN',
  'western armenian': 'AM',
  'western yiddish': 'IL',
  yemeni: 'YE',
  'yemeni arabic': 'YE',
  yiddish: 'IL',
  zulu: 'ZA',
};

const SUBDIVISION_FLAG_CODES: Record<string, string> = {
  england: 'gbeng',
  scotland: 'gbsct',
  scottish: 'gbsct',
  wales: 'gbwls',
  welsh: 'gbwls',
};

const LANGUAGE_FLAG_MATCHES = Object.entries({
  ...LANGUAGE_FLAG_CODES,
  ...SUBDIVISION_FLAG_CODES,
}).sort(([left], [right]) => right.length - left.length);

function normalizeFlagLookup(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

const KNOWN_LANGUAGE_LABELS = new Map<string, string>();
const WORD_LABEL_OVERRIDES: Record<string, string> = {
  asl: 'ASL',
};

function registerKnownLanguageLabel(value: string) {
  const label = String(value || '')
    .trim()
    .replace(/\s+/g, ' ');
  if (!label) return;
  const key = normalizeFlagLookup(label);
  if (key && !KNOWN_LANGUAGE_LABELS.has(key)) {
    KNOWN_LANGUAGE_LABELS.set(key, label);
  }
}

Object.entries(SUPPORTED_LANGUAGES).forEach(([language, dialects]) => {
  registerKnownLanguageLabel(language);
  dialects.forEach(registerKnownLanguageLabel);
});

TELEPHONIC_SUPPORTED_LANGUAGES.forEach(entry => {
  if (typeof entry === 'string') {
    registerKnownLanguageLabel(entry);
    return;
  }

  Object.entries(entry).forEach(([language, dialects]) => {
    registerKnownLanguageLabel(language);
    dialects.forEach(registerKnownLanguageLabel);
  });
});

export function displayLanguageName(
  language: string | null | undefined,
): string {
  const normalized = String(language || '')
    .trim()
    .replace(/\s+/g, ' ');
  if (!normalized) return '';

  const knownLabel = KNOWN_LANGUAGE_LABELS.get(normalizeFlagLookup(normalized));
  if (knownLabel) return knownLabel;

  return normalized.replace(/[A-Za-z0-9']+/g, word => {
    const override = WORD_LABEL_OVERRIDES[word.toLowerCase()];
    if (override) return override;
    if (word.length <= 3 && word === word.toUpperCase()) return word;
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  });
}

function flagFromCode(code: string): string {
  return /^[A-Z]{2}$/.test(code)
    ? unicodeRegionFlag(code)
    : unicodeSubdivisionFlag(code);
}

export function languageFlag(language: string | null | undefined): string {
  if (!language) return FLAG_FALLBACK;
  const normalized = normalizeFlagLookup(language);
  for (const [key, code] of LANGUAGE_FLAG_MATCHES) {
    if (normalized.includes(key)) {
      return flagFromCode(code);
    }
  }
  return FLAG_FALLBACK;
}

// ── Shared helpers ───────────────────────────────────────────
export function initials(
  firstName: string | null | undefined,
  lastName?: string | null,
): string {
  const f = (firstName || '').charAt(0).toUpperCase();
  const l = (lastName || '').charAt(0).toUpperCase();
  return l ? `${f}${l}` : f || '?';
}

export function displayName(
  firstName: string | null | undefined,
  lastName?: string | null,
): string {
  if (!lastName) return firstName || 'Unknown';
  return `${lastName}, ${firstName}`;
}

export function fuzzyMatch(
  needle: string | null | undefined,
  haystack: string | null | undefined,
): boolean {
  if (!needle || !haystack) return false;
  return haystack.toLowerCase().includes(needle.toLowerCase());
}

// Dates arrive from Firestore Timestamps, ISO strings, epoch millis, or
// Date objects depending on the caller — accept anything and parse defensively.
interface DateLike {
  toDate?: () => Date;
  seconds?: number;
}

export function formatShortDate(ts: unknown): string {
  if (!ts) return '';
  const t = ts as DateLike;
  const d =
    typeof t.toDate === 'function'
      ? t.toDate()
      : new Date(ts as string | number | Date);
  return d.toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: '2-digit',
  });
}

export function isNonEnglish(language: string | null | undefined): boolean {
  if (!language) return false;
  return language.toLowerCase() !== 'english';
}

function parseDob(dob: unknown): Date | null {
  if (!dob) return null;
  if (dob instanceof Date) return isNaN(dob.getTime()) ? null : dob;
  const t = dob as DateLike;
  if (typeof t.toDate === 'function') return t.toDate();
  if (typeof t.seconds === 'number') return new Date(t.seconds * 1000);
  if (typeof dob === 'string') {
    const dateOnly = dob.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnly) {
      const [, year, month, day] = dateOnly;
      return new Date(Number(year), Number(month) - 1, Number(day));
    }
  }
  const d = new Date(dob as string | number);
  return isNaN(d.getTime()) ? null : d;
}

// Convert a native <input type="date"> value ("YYYY-MM-DD") into a Date at
// local midnight. `new Date("YYYY-MM-DD")` parses as UTC midnight, which for
// users west of UTC shifts the stored DOB to the previous calendar day
// (e.g. 11/26 saved/shown as 11/25). Building from explicit parts keeps it
// on the correct local day in every timezone.
export function dateInputToLocalDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value !== 'string') return null;
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    const fallback = new Date(value);
    return isNaN(fallback.getTime()) ? null : fallback;
  }
  const date = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
  );
  return isNaN(date.getTime()) ? null : date;
}

export function computeAge(dob: unknown): number | null {
  const d = parseDob(dob);
  if (!d) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const monthDiff = now.getMonth() - d.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < d.getDate())) age--;
  return age >= 0 ? age : null;
}

export function formatDob(dob: unknown): string {
  const d = parseDob(dob);
  if (!d) return '';
  return d.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

// Compact DOB used in dense rows (search results / pickers) where we want
// just "MM/DD/YYYY" — saves horizontal space compared to formatDob.
export function formatDobShort(dob: unknown): string {
  const d = parseDob(dob);
  if (!d) return '';
  return d.toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
  });
}

export function truncateMrn(mrn: string | null | undefined): string {
  if (!mrn) return '';
  if (mrn.length <= 12) return mrn;
  return `${mrn.slice(0, 8)}\u2026${mrn.slice(-4)}`;
}

// ── Animation presets (matching iOS spring personality) ───────
export const listStaggerInterval = 0.04; // seconds between items
